/** Вход / регистрация / вход по ссылке из письма (с кодом — для установленного PWA). */
import { useState, type FormEvent } from 'react';
import { supabase } from '../data/supabase.ts';
import { authErrorText } from '../state/session.tsx';
import { RoboCat, type RoboCatState } from '../ui/RoboCat.tsx';
import './auth.css';

type Mode = 'login' | 'register' | 'magic';

const MODES: readonly { id: Mode; label: string }[] = [
  { id: 'login', label: 'Вход' },
  { id: 'register', label: 'Регистрация' },
  { id: 'magic', label: 'По ссылке' },
];

export function AuthScreen() {
  const [mode, setMode] = useState<Mode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [linkSent, setLinkSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [typing, setTyping] = useState(false);

  const run = async (action: () => Promise<{ error: { message: string } | null }>, onSuccess?: () => void) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const { error: authError } = await action();
      if (authError) setError(authErrorText(authError.message));
      else onSuccess?.();
    } catch (e) {
      setError(authErrorText(e instanceof Error ? e.message : ''));
    } finally {
      setBusy(false);
    }
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const address = email.trim();
    if (mode === 'login') {
      void run(() => supabase.auth.signInWithPassword({ email: address, password }));
    } else if (mode === 'register') {
      void run(
        () =>
          supabase.auth.signUp({
            email: address,
            password,
            options: { data: { display_name: name.trim() }, emailRedirectTo: window.location.origin },
          }),
        () => setNotice('Готово! Если пришло письмо — подтвердите почту и войдите.'),
      );
    } else if (!linkSent) {
      void run(
        () => supabase.auth.signInWithOtp({ email: address, options: { emailRedirectTo: window.location.origin } }),
        () => setLinkSent(true),
      );
    } else {
      void run(() => supabase.auth.verifyOtp({ email: address, token: code.trim(), type: 'email' }));
    }
  };

  const catState: RoboCatState = busy ? 'thinking' : error ? 'sad' : typing ? 'listening' : 'idle';
  const submitLabel =
    mode === 'login' ? 'Войти' : mode === 'register' ? 'Создать аккаунт' : linkSent ? 'Войти с кодом' : 'Прислать ссылку';

  return (
    <div className="auth th-cn">
      <div className="auth__hero">
        <RoboCat size={132} state={catState} typedLength={email.length} followPointer label="Робо-кот Сөйле" />
        <h1 className="auth__brand">Сөйле</h1>
        <p className="t-meta">Китайский, английский и турецкий — каждый день понемногу.</p>
      </div>

      <div className="card auth__card">
        <div className="auth__modes" role="tablist" aria-label="Способ входа">
          {MODES.map((m) => (
            <button
              key={m.id}
              type="button"
              role="tab"
              aria-selected={mode === m.id}
              className={`auth__mode${mode === m.id ? ' auth__mode--on' : ''}`}
              onClick={() => {
                setMode(m.id);
                setError(null);
                setNotice(null);
                setLinkSent(false);
              }}
            >
              {m.label}
            </button>
          ))}
        </div>

        <form className="stack" onSubmit={submit}>
          {mode === 'register' && (
            <label className="field">
              <span className="field__label">Имя</span>
              <input className="input" value={name} onChange={(e) => setName(e.target.value)} autoComplete="nickname" />
            </label>
          )}
          <label className="field">
            <span className="field__label">Почта</span>
            <input
              className="input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onFocus={() => setTyping(true)}
              onBlur={() => setTyping(false)}
              autoComplete="email"
              inputMode="email"
              disabled={linkSent}
            />
          </label>
          {mode !== 'magic' && (
            <label className="field">
              <span className="field__label">Пароль</span>
              <input
                className="input"
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
              />
            </label>
          )}
          {mode === 'magic' && linkSent && (
            <>
              <p className="t-meta">
                Письмо отправлено. Откройте ссылку на этом устройстве или введите код из письма — так удобнее в
                установленном приложении.
              </p>
              <label className="field">
                <span className="field__label">Код из письма</span>
                <input
                  className="input"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                />
              </label>
            </>
          )}
          {error && (
            <p className="form-error" role="alert">
              {error}
            </p>
          )}
          {notice && (
            <p className="t-meta" role="status">
              {notice}
            </p>
          )}
          <button
            type="submit"
            className="btn btn--ink btn--block"
            disabled={busy || (mode === 'magic' && linkSent && code.trim().length < 6)}
          >
            {submitLabel}
          </button>
        </form>
      </div>
    </div>
  );
}
